import { Component, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterLink } from '@angular/router';

type FilterTab = 'all' | 'makki' | 'madani' | 'juz' | 'recent' | 'favorites';

interface Surah {
  number: number;
  arabic: string;
  name: string;
  translation: string;
  ayahs: number;
  type: 'makki' | 'madani';
}

interface JuzItem { number: number; pages: string; }

@Component({
  selector: 'app-quran',
  imports: [CommonModule, RouterLink],
  templateUrl: './quran.component.html',
  styleUrl: './quran.component.scss'
})
export class QuranComponent {

  protected activeFilter = signal<FilterTab>('all');

  protected readonly continueReading = {
    juz: 3, page: 42,
    arabic: 'وَأَنتُمُ الْأَعْلَوْنَ إِن كُنتُم مُّؤْمِنِينَ',
    surah: 'Surah Al-Imran (3:139)',
    percent: 60,
  };

  protected readonly todaysGoal = { read: 1, total: 2 };

  protected readonly quickActions = [
    { icon: '📖', label: 'Last Read' },
    { icon: '🔖', label: 'Bookmarks' },
    { icon: '📝', label: 'Notes' },
    { icon: '✏️', label: 'Highlights' },
  ];

  protected readonly quickTools = [
    { icon: '📜', label: 'Read Quran', sub: 'Mushaf' },
    { icon: '🔍', label: 'Tafsir', sub: 'Understand' },
    { icon: '🌐', label: 'Translation', sub: 'Multiple Languages' },
    { icon: '🎧', label: 'Audio', sub: 'Listen & Learn' },
    { icon: '🔤', label: 'Word by Word', sub: 'Learn Meanings' },
    { icon: '⋯',  label: 'More', sub: '' },
  ];

  protected readonly filterTabs: { key: FilterTab; label: string }[] = [
    { key: 'all',       label: 'All Surahs' },
    { key: 'makki',     label: 'Makki' },
    { key: 'madani',    label: 'Madani' },
    { key: 'juz',       label: "Juz'" },
    { key: 'recent',    label: 'Recently Read' },
    { key: 'favorites', label: '♡ Surah Favorites' },
  ];

  protected readonly allSurahs: Surah[] = [
    { number:  1, arabic: 'سُورَةُ الْفَاتِحَةِ', name: 'Al-Fatihah',  translation: 'The Opening',       ayahs:   7, type: 'makki' },
    { number:  2, arabic: 'سُورَةُ الْبَقَرَةِ', name: 'Al-Baqarah',   translation: 'The Cow',            ayahs: 286, type: 'madani' },
    { number:  3, arabic: 'سُورَةُ آلِ عِمْرَانَ', name: "Ali 'Imran", translation: 'The Family of Imran', ayahs: 200, type: 'madani' },
    { number:  4, arabic: 'سُورَةُ النِّسَاءِ', name: 'An-Nisa',       translation: 'The Women',          ayahs: 176, type: 'madani' },
    { number:  5, arabic: 'سُورَةُ الْمَائِدَةِ', name: 'Al-Ma\'idah', translation: 'The Table Spread',   ayahs: 120, type: 'madani' },
    { number:  6, arabic: 'سُورَةُ الْأَنْعَامِ', name: "Al-An'am",    translation: 'The Cattle',         ayahs: 165, type: 'makki' },
    { number:  7, arabic: 'سُورَةُ الْأَعْرَافِ', name: "Al-A'raf",    translation: 'The Heights',        ayahs: 206, type: 'makki' },
    { number:  8, arabic: 'سُورَةُ الْأَنْفَالِ', name: 'Al-Anfal',    translation: 'The Spoils of War',  ayahs:  75, type: 'madani' },
    { number:  9, arabic: 'سُورَةُ التَّوْبَةِ', name: 'At-Tawbah',    translation: 'The Repentance',     ayahs: 129, type: 'madani' },
    { number: 10, arabic: 'سُورَةُ يُونُسَ', name: 'Yunus',           translation: 'Jonah',              ayahs: 109, type: 'makki' },
    { number: 11, arabic: 'سُورَةُ هُودٍ', name: 'Hud',               translation: 'Hud',                ayahs: 123, type: 'makki' },
    { number: 12, arabic: 'سُورَةُ يُوسُفَ', name: 'Yusuf',           translation: 'Joseph',             ayahs: 111, type: 'makki' },
  ];

  protected get filteredSurahs(): Surah[] {
    const f = this.activeFilter();
    if (f === 'makki')  return this.allSurahs.filter(s => s.type === 'makki');
    if (f === 'madani') return this.allSurahs.filter(s => s.type === 'madani');
    return this.allSurahs;
  }

  protected readonly dailyVerse = {
    arabic: 'إِنَّ مَعَ الْعُسْرِ يُسْرًا',
    english: 'Indeed, with hardship [will be] ease.',
    reference: 'Surah Ash-Sharh (94:6)',
  };

  protected readonly stats = {
    juzRead: 2, juzTotal: 30,
    pagesRead: 20, pagesTotal: 604,
    versesRead: 150, versesTotal: 6236,
    timeSpent: '3h 25m',
  };

  protected readonly juzList: JuzItem[] = [
    { number: 1, pages: 'Pages 1-20' },
    { number: 2, pages: 'Pages 21-40' },
    { number: 3, pages: 'Pages 41-60' },
    { number: 4, pages: 'Pages 61-80' },
    { number: 5, pages: 'Pages 81-100' },
  ];

  protected readonly features = [
    { icon: '📖', title: 'Beautiful Mushaf',      desc: 'Clear & Elegant Script' },
    { icon: '💡', title: 'Tafsir Insights',        desc: 'Understand the meanings deeply' },
    { icon: '🌐', title: 'Multiple Translations',  desc: 'Read in your preferred language' },
    { icon: '🎧', title: 'Audio Recitations',      desc: 'Listen to beautiful recitations' },
    { icon: '📥', title: 'Offline Access',         desc: 'Download & read anytime, anywhere' },
  ];

  protected readonly tipOfDay = 'Read a little, reflect more and implement in life.';

  protected setFilter(f: FilterTab): void {
    this.activeFilter.set(f);
  }

  protected get surahPairs(): [Surah, Surah | null][] {
    const list = this.filteredSurahs;
    const pairs: [Surah, Surah | null][] = [];
    for (let i = 0; i < list.length; i += 2) {
      pairs.push([list[i], list[i + 1] ?? null]);
    }
    return pairs;
  }
}
